import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Card,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import {
  GetApp as GetAppIcon,
  Settings as SettingsIcon
} from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import CardTitle from '../../../common/card/CardTitle';
import WrappedTableCell from '../../../common/WrappedTableCell';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import DocumentDownloadButton from '../../documents/components/DocumentDownloadButton';
import { Hazard, HazardExportEntity, HazardSection } from '../Hazard.interface';
import {
  getExportTitle,
  useDownloadHazardDefaultTemplate,
  useUpdateHazard
} from '../Hazard.hooks';
import { getHazardTemplateFieldId } from '../Hazard.helpers';
import HazardAssignDocumentTemplateDialog from '../dialogs/HazardAssignDocumentTemplateDialog';

const StyledTableCell = styled(TableCell)`
  .MuiButtonBase-root {
    margin-right: 8px;
  }
`;

export interface ManageHazardTemplateCardProps {
  hazard: Hazard;
  onChange?: (updatedHazard: Partial<Hazard>) => void;
}

const ManageHazardTemplateCard: React.FC<ManageHazardTemplateCardProps> = (
  props: ManageHazardTemplateCardProps
) => {
  const { hazard, onChange } = props;
  const entityRef = useRef<HazardExportEntity | undefined>();
  const [error, setError] = useState<FetchError | undefined>();
  const [assignEntity, setAssignEntity] = useState<
    HazardExportEntity | undefined
  >();
  const [clearTemplate, setClearTemplate] = useState<
    HazardSection | undefined
  >();
  const { updatedHazard, updateError, updateHazard } = useUpdateHazard(
    hazard.id
  );
  const [
    { isLoading: isDownloading, error: downloadError, response },
    getDocument,
    downloadDocument
  ] = useDownloadHazardDefaultTemplate();

  const sections: HazardSection[] = useMemo(() => {
    return [
      { file: hazard.causeTemplate, entity: HazardExportEntity.Cause },
      {
        file: hazard.controlTemplate,
        entity: HazardExportEntity.Control
      },
      {
        file: hazard.actionTemplate,
        entity: HazardExportEntity.Action
      }
    ];
  }, [hazard]);

  useEffect(() => {
    if (updatedHazard) {
      onChange?.(updatedHazard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedHazard]);

  useEffect(() => {
    if (response?.ok && entityRef.current) {
      const fileName: string =
        getExportTitle(entityRef.current) + ' Template.docx';
      downloadDocument(fileName);
      entityRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    setError(updateError || downloadError);
  }, [updateError, downloadError]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <Card>
          <CardTitle>
            <SettingsIcon />
            Manage Templates
          </CardTitle>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Section</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell width={1}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.map((section: HazardSection) => {
                  const { file, entity } = section;
                  const storageKey: string | undefined =
                    file?.fileVersion?.storageKey ?? file?.storageKey;
                  const isLoading: boolean =
                    isDownloading && entityRef.current === entity;
                  return (
                    <TableRow key={entity}>
                      <WrappedTableCell>
                        {getExportTitle(entity)}
                      </WrappedTableCell>
                      <WrappedTableCell>
                        {file?.path ? (
                          <Link
                            to={`/portal/documents/file/task/${file.taskId}`}
                          >
                            {file.path}
                          </Link>
                        ) : (
                          <Typography variant='body2' color='textSecondary'>
                            Default
                          </Typography>
                        )}
                      </WrappedTableCell>
                      <StyledTableCell>
                        {storageKey ? (
                          <DocumentDownloadButton
                            title={file?.name}
                            storageKey={storageKey}
                          />
                        ) : (
                          <Button
                            color='primary'
                            variant='contained'
                            disabled={isLoading}
                            endIcon={
                              isLoading ? (
                                <CircularProgress color='primary' size={20} />
                              ) : (
                                <GetAppIcon />
                              )
                            }
                            onClick={() => {
                              entityRef.current = entity;
                              getDocument({ entity });
                            }}
                          >
                            Download
                          </Button>
                        )}

                        <Button
                          color='primary'
                          variant='contained'
                          onClick={() => setAssignEntity(entity)}
                        >
                          Select
                        </Button>
                        <Button
                          color='primary'
                          variant='contained'
                          disabled={!file}
                          onClick={() => setClearTemplate(section)}
                        >
                          Clear
                        </Button>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
      {assignEntity && (
        <HazardAssignDocumentTemplateDialog
          open={!!assignEntity}
          hazard={hazard}
          assignEntity={assignEntity}
          onCancel={() => setAssignEntity(undefined)}
          onSuccess={(hazard: Hazard) => {
            setAssignEntity(undefined);
            onChange?.(hazard);
          }}
        />
      )}
      {clearTemplate && (
        <ConfirmDialog
          open={!!clearTemplate}
          title='Clear Document Template'
          message={`Are you sure you want to clear ${getExportTitle(
            clearTemplate.entity
          )} template?`}
          onCancel={() => setClearTemplate(undefined)}
          onConfirm={() => {
            const field = getHazardTemplateFieldId(clearTemplate.entity);
            updateHazard({ [field]: null });
            setClearTemplate(undefined);
          }}
        />
      )}

      <AppSnackbar severity='error' open={!!error}>
        {error?.message || 'An error occurred while exporting the document.'}
      </AppSnackbar>
    </Grid>
  );
};

export default ManageHazardTemplateCard;
